import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '../../hooks.ts';
import { getUserFeatureFlags } from '../../store/selectors/featureFlags.selector.ts';
import { greyP5 } from '../../theme.ts';
import { ONLINE_PAYMENTS_FLAG } from '../../utils/featureFlagKeys.ts';

interface SettingsSidebarProps {
  activeSection?: string;
  onSectionChange?: (section: string) => void;
}

export const SettingsSidebar: React.FC<SettingsSidebarProps> = ({ activeSection = 'Account', onSectionChange }) => {
  const { t } = useTranslation();
  const paymentsEnabled = useAppSelector((state) => getUserFeatureFlags(state).includes(ONLINE_PAYMENTS_FLAG));
  const sections = [
    { name: 'Account', label: t('settings.sidebar.account') },
    { name: 'Project', label: t('settings.sidebar.project') },
    { name: 'UI Settings', label: t('settings.sidebar.ui') },
    { name: 'Plans & Subscriptions', label: t('settings.sidebar.plans') },
    { name: 'Change password', label: t('settings.sidebar.password') },
    {
      name: 'Billing',
      label: t('settings.sidebar.billing'),
      comingSoon: paymentsEnabled ? undefined : (
        <span className="bg-greyP5 text-black text-[10px] px-2 font-extrabold p-1 rounded-full">
          {t('common.comingSoon')}
        </span>
      ),
    },
    {
      name: 'Request/Problems',
      label: t('settings.sidebar.requests'),
      comingSoon: (
        <span className="bg-greyP5 text-black text-[10px] px-2 font-extrabold p-1 rounded-full">
          {t('common.comingSoon')}
        </span>
      ),
    },
  ];

  return (
    <nav
      className="flex-shrink-0 flex flex-row md:flex-col border-b md:border-b-0 md:border-r overflow-x-auto md:overflow-x-visible w-full md:w-[240px] md:h-[calc(100vh-112px)]"
      style={{
        minWidth: 0,
        borderColor: greyP5,
      }}
      data-testid="settings-sidebar-nav"
    >
      <div className="bg-black overflow-y-auto md:h-full">
        <div className="hidden md:flex h-16 items-center px-8 border-b border-greyP5">
          <span className="text-greyP6 text-xl font-normal family-azbuka">{t('settings.title')}</span>
        </div>
        <div className="flex flex-row md:flex-col">
          <div className="flex flex-row md:flex-col md:space-y-0">
            {sections.map((section) => (
              <button
                disabled={!!section.comingSoon}
                key={section.name}
                onClick={() => onSectionChange?.(section.name)}
                className={`flex flex-row items-center justify-between whitespace-nowrap md:w-full h-[44px] gap-2 text-left
                  disabled:cursor-not-allowed 
                  px-4 md:px-8 text-white font-medium border-r md:border-r-0 md:border-b border-greyP5 transition-colors duration-200 text-xs ${
                    activeSection === section.name ? 'bg-greyP5' : 'hover:bg-greyP5'
                  }`}
              >
                {section.label}
                <span className="hidden md:inline">{section.comingSoon}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};
